/**
 * Dev / legacy DB fallback when `community_ghg_inventory_release` is not migrated yet.
 * Allowlisted in scripts/check-no-ghg-precalc-runtime.mjs — not used when release table exists.
 */
import { inventoryGeofipsNumeric } from './ghgInventoryGeofipsCanonical';
import { supabaseClient } from './supabase';

type ReleaseGeographyFilter = 'City' | 'County' | 'State';

export interface PrecalcFallbackFetchOptions {
  year?: number;
  yearMin?: number;
  yearMax?: number;
  geographyType: ReleaseGeographyFilter;
  geofipsIn?: number[];
  select?: string;
}

export interface PrecalcFallbackReleaseRow {
  geofips: number;
  geofipsRaw: unknown;
  location_name: string;
  geography_type: string;
  year: number;
  calculation_date: string | null;
  calculation_version: string | null;
  total_emissions: number | null;
  ldv_emissions: number | null;
  hdv_emissions: number | null;
  elec_total_emissions: number | null;
  elec_residential_emissions: number | null;
  elec_commercial_emissions: number | null;
  ng_total_emissions: number | null;
  ng_residential_emissions: number | null;
  ng_commercial_emissions: number | null;
  waste_emissions: number | null;
  offroad_emissions: number | null;
  agriculture_emissions: number | null;
  elec_residential_kwh: number | null;
  elec_commercial_kwh: number | null;
  ng_residential_therms: number | null;
  ng_commercial_therms: number | null;
  ldv_vmt: number | null;
}

const PRECALC_RELATION = 'ghg_inventory_precalculated';
const PAGE_SIZE = 1000;

const DEFAULT_SECTOR_SELECT =
  'geofips,location_name,geography_type,year,calculation_date,calculation_version,total_emissions,ldv_emissions,hdv_emissions,elec_total_emissions,elec_residential_emissions,elec_commercial_emissions,ng_total_emissions,ng_residential_emissions,ng_commercial_emissions,waste_emissions,offroad_emissions,agriculture_emissions,elec_residential_kwh,elec_commercial_kwh,ng_residential_therms,ng_commercial_therms,ldv_vmt';

function numNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export async function fetchPrecalcRowsAsRelease(
  options: PrecalcFallbackFetchOptions
): Promise<{ rows: PrecalcFallbackReleaseRow[]; error: string | null }> {
  const select = options.select ?? DEFAULT_SECTOR_SELECT;
  const raw: PrecalcFallbackReleaseRow[] = [];
  let from = 0;

  for (;;) {
    let q = supabaseClient.from(PRECALC_RELATION).select(select).order('geofips', { ascending: true });

    if (options.year != null) q = q.eq('year', options.year);
    if (options.yearMin != null) q = q.gte('year', options.yearMin);
    if (options.yearMax != null) q = q.lte('year', options.yearMax);

    if (options.geofipsIn?.length) {
      q = q.in('geofips', options.geofipsIn);
    } else if (options.geographyType === 'County') {
      q = q.eq('geography_type', 'County').gte('geofips', 6000).lt('geofips', 7000);
    } else if (options.geographyType === 'City') {
      q = q.eq('geography_type', 'City').gte('geofips', 600000).lt('geofips', 700000);
    } else {
      q = q.eq('geography_type', 'State');
    }

    const { data, error } = await q.range(from, from + PAGE_SIZE - 1);
    if (error) return { rows: [], error: error.message };
    const batch = (data ?? []) as unknown as Record<string, unknown>[];
    if (batch.length === 0) break;

    for (const r of batch) {
      const gf = inventoryGeofipsNumeric(r.geofips);
      if (gf == null) continue;
      raw.push({
        geofips: gf,
        geofipsRaw: r.geofips,
        location_name: String(r.location_name || '').trim(),
        geography_type: String(r.geography_type || ''),
        year: Number(r.year),
        calculation_date: r.calculation_date != null ? String(r.calculation_date) : null,
        calculation_version: r.calculation_version != null ? String(r.calculation_version) : null,
        total_emissions: numNull(r.total_emissions),
        ldv_emissions: numNull(r.ldv_emissions),
        hdv_emissions: numNull(r.hdv_emissions),
        elec_total_emissions: numNull(r.elec_total_emissions),
        elec_residential_emissions: numNull(r.elec_residential_emissions),
        elec_commercial_emissions: numNull(r.elec_commercial_emissions),
        ng_total_emissions: numNull(r.ng_total_emissions),
        ng_residential_emissions: numNull(r.ng_residential_emissions),
        ng_commercial_emissions: numNull(r.ng_commercial_emissions),
        waste_emissions: numNull(r.waste_emissions),
        offroad_emissions: numNull(r.offroad_emissions),
        agriculture_emissions: numNull(r.agriculture_emissions),
        elec_residential_kwh: numNull(r.elec_residential_kwh),
        elec_commercial_kwh: numNull(r.elec_commercial_kwh),
        ng_residential_therms: numNull(r.ng_residential_therms),
        ng_commercial_therms: numNull(r.ng_commercial_therms),
        ldv_vmt: numNull(r.ldv_vmt),
      });
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return { rows: raw, error: null };
}
